/**
 * QR Storefront — Checkout Intercept + Share Modal
 * Cherry-pickable. Runs on any page with ?s= param.
 *
 * Intercepts checkout button → builds cart permalink → shows QR share modal.
 * The share modal lets the member share the cart with customers.
 *
 * Translations are read from data-t-* attributes on #qr-share-modal element,
 * set by the Liquid snippet via section settings.
 */

(function () {
  "use strict";

  var params = new URLSearchParams(window.location.search);
  var sParam = params.get("s");
  if (!sParam) return;

  // Flag the document so CSS rules (e.g. object-fit: contain on cards) can
  // scope to "member storefront mode" without affecting the rest of the theme.
  if (document.documentElement) {
    document.documentElement.classList.add("qr-storefront-active");
  }

  var root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";

  // Two entry modes:
  //   - mode=sell  → the team leader is on their own storefront to make an
  //                  in-person (door-to-door) sale. Intercept checkout and
  //                  show the QR share modal so the buyer scans to pay.
  //   - no mode    → a buyer followed a leader's shared link. Just write
  //                  the attribution to the cart so the order is credited
  //                  to the member, and let them check out normally.
  var mode = params.get("mode");
  var isSellMode = mode === "sell";

  // Persist attribution into cart attributes on page load. Fire-and-forget;
  // harmless if cart.attributes.s is already set to the same value.
  function writeAttribution() {
    var body = new URLSearchParams();
    body.set("attributes[s]", sParam);
    fetch(root + "cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: body.toString(),
    }).catch(function () {});
  }
  writeAttribution();

  // ── Translation helper ──────────────────────────────────────────────────────

  function getT(key, fallback) {
    var modal = document.getElementById("qr-share-modal");
    if (!modal) return fallback;
    return modal.dataset["t" + key.charAt(0).toUpperCase() + key.slice(1)] || fallback;
  }

  // ── Intercept checkout ─────────────────────────────────────────────────────
  // Use capture-phase delegation on document so we catch every checkout click
  // regardless of when the button was added to the DOM or what class it has.

  var CHECKOUT_SELECTOR = [
    'a[href*="/checkout"]',
    '[name="checkout"]',
    'form[action*="/checkout"] [type="submit"]',
    ".xo-cart-drawer__checkout",
    ".cart__checkout-button",
    "[data-cart-checkout]",
  ].join(",");

  function interceptCheckout() {
    document.addEventListener("click", function (e) {
      var target = e.target;
      // Walk up the DOM tree in case the click lands on a child element (e.g. <span> inside a <button>)
      while (target && target !== document.body) {
        if (target.matches && target.matches(CHECKOUT_SELECTOR)) {
          handleCheckoutClick(e);
          return;
        }
        target = target.parentElement;
      }
    }, true /* capture phase — fires before the element's own handlers */);
  }

  function handleCheckoutClick(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    fetch(root + "cart.js", {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) {
          alert(getT("cartEmpty", "Your cart is empty."));
          return;
        }

        var permalink = buildCartPermalink(cart);
        showShareModal(permalink);
      })
      .catch(function (err) {
        console.error("[QR Storefront] Failed to read cart:", err);
        alert(getT("error", "Something went wrong. Please try again."));
      });
  }

  function buildCartPermalink(cart) {
    var items = cart.items
      .map(function (item) {
        return item.variant_id + ":" + item.quantity;
      })
      .join(",");

    var shopDomain = window.location.origin;
    return (
      shopDomain +
      "/cart/" +
      items +
      "?attributes[s]=" +
      encodeURIComponent(sParam)
    );
  }

  // ── Share Modal ────────────────────────────────────────────────────────────

  // Ensure share modal is a direct child of <body> to escape any stacking context
  var _modalMoved = false;
  function ensureModalOnBody() {
    if (_modalMoved) return;
    var modal = document.getElementById("qr-share-modal");
    if (modal && modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }
    _modalMoved = true;
  }

  function showShareModal(permalink) {
    // Close cart drawer using the theme's own API
    if (window.xoModal) {
      window.xoModal.close("cart");
    }

    ensureModalOnBody();

    var modal = document.getElementById("qr-share-modal");
    if (!modal) return;

    var copyLabel = getT("copyBtn", "Copy Link");
    var copiedLabel = getT("copied", "Copied!");

    var linkInput = document.getElementById("qr-share-modal-link");
    var canvas = document.getElementById("qr-share-modal-canvas");
    var copyBtn = document.getElementById("qr-share-modal-copy");
    var downloadBtn = document.getElementById("qr-share-modal-download");
    var closeBtn = document.getElementById("qr-share-modal-close-btn");
    var backdrop = modal.querySelector(".qr-share-modal__backdrop");
    var closeX = modal.querySelector(".qr-share-modal__close");

    linkInput.value = permalink;
    modal.style.display = "flex";
    generateQR(canvas, permalink);

    function onCopy() {
      navigator.clipboard
        .writeText(permalink)
        .then(function () {
          copyBtn.textContent = copiedLabel;
          setTimeout(function () {
            copyBtn.textContent = copyLabel;
          }, 2000);
        })
        .catch(function () {
          linkInput.select();
          document.execCommand("copy");
          copyBtn.textContent = copiedLabel;
          setTimeout(function () {
            copyBtn.textContent = copyLabel;
          }, 2000);
        });
    }

    function onDownload() {
      var dataUrl = canvas.toDataURL("image/png");
      var a = document.createElement("a");
      a.href = dataUrl;
      a.download = "cart-qr-code.png";
      a.click();
    }

    function onEsc(e) {
      if (e.key === "Escape") close();
    }

    function close() {
      modal.style.display = "none";
      copyBtn.removeEventListener("click", onCopy);
      downloadBtn.removeEventListener("click", onDownload);
      closeBtn.removeEventListener("click", close);
      closeX.removeEventListener("click", close);
      backdrop.removeEventListener("click", close);
      document.removeEventListener("keydown", onEsc);
    }

    copyBtn.addEventListener("click", onCopy);
    downloadBtn.addEventListener("click", onDownload);
    closeBtn.addEventListener("click", close);
    closeX.addEventListener("click", close);
    backdrop.addEventListener("click", close);
    document.addEventListener("keydown", onEsc);
  }

  // ── QR Code Generator (uses qrcode-generator lib) ────────────────────────

  var _qrLibLoaded = false;
  var _qrLibCallbacks = [];

  function loadQRLib(callback) {
    if (_qrLibLoaded && window.qrcode) {
      callback();
      return;
    }
    _qrLibCallbacks.push(callback);
    if (_qrLibCallbacks.length > 1) return; // already loading

    var script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js";
    script.onload = function () {
      _qrLibLoaded = true;
      _qrLibCallbacks.forEach(function (cb) { cb(); });
      _qrLibCallbacks = [];
    };
    script.onerror = function () {
      _qrLibCallbacks.forEach(function (cb) { cb(); });
      _qrLibCallbacks = [];
    };
    document.head.appendChild(script);
  }

  function generateQR(canvas, text) {
    var size = 280;
    canvas.width = size;
    canvas.height = size;
    var ctx = canvas.getContext("2d");

    var tGenerating = getT("generating", "Generating QR...");
    var tUnavailable = getT("unavailable", "QR code unavailable");
    var tUseLink = getT("useLink", "Use the link below");

    // Show loading state
    ctx.fillStyle = "#f9f9f9";
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = "#6d7175";
    ctx.font = "13px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(tGenerating, size / 2, size / 2);

    loadQRLib(function () {
      if (!window.qrcode) {
        ctx.fillStyle = "#f9f9f9";
        ctx.fillRect(0, 0, size, size);
        ctx.fillStyle = "#6d7175";
        ctx.font = "13px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(tUnavailable, size / 2, size / 2 - 10);
        ctx.fillText(tUseLink, size / 2, size / 2 + 10);
        return;
      }

      try {
        // typeNumber 0 = auto-detect, errorCorrectionLevel M
        var qr = window.qrcode(0, "M");
        qr.addData(text);
        qr.make();

        var moduleCount = qr.getModuleCount();
        var quietZone = 4;
        var totalModules = moduleCount + quietZone * 2;
        var cellSize = size / totalModules;

        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, size, size);
        ctx.fillStyle = "#000";

        for (var r = 0; r < moduleCount; r++) {
          for (var c = 0; c < moduleCount; c++) {
            if (qr.isDark(r, c)) {
              ctx.fillRect(
                Math.round((c + quietZone) * cellSize),
                Math.round((r + quietZone) * cellSize),
                Math.ceil(cellSize),
                Math.ceil(cellSize)
              );
            }
          }
        }
      } catch (e) {
        console.error("[QR Storefront] QR generation failed:", e);
        ctx.fillStyle = "#f9f9f9";
        ctx.fillRect(0, 0, size, size);
        ctx.fillStyle = "#6d7175";
        ctx.font = "13px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(tUnavailable, size / 2, size / 2 - 10);
        ctx.fillText(tUseLink, size / 2, size / 2 + 10);
      }
    });
  }

  // ── Block product-card navigation ──────────────────────────────────────────
  // Customers must stay on the club storefront — the only allowed exit is
  // checkout (handled above). Quick-add and quick-view buttons are <button>
  // elements and keep working; only anchor navigation inside a product card
  // is blocked. Runs document-wide so it works on collection pages, the
  // custom /pages/store page, search results, etc.

  function blockCardNavigation() {
    document.addEventListener(
      "click",
      function (e) {
        var anchor = e.target && e.target.closest ? e.target.closest("a") : null;
        if (!anchor) return;

        // Only block clicks that originated inside a product card
        var card = anchor.closest(".xo-product-card");
        if (!card) return;

        // Checkout links go through interceptCheckout() instead
        var href = anchor.getAttribute("href") || "";
        if (/\/checkout/.test(href)) return;

        e.preventDefault();
        e.stopPropagation();
      },
      true /* capture phase — beats the theme's own handlers */
    );
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  function init() {
    // Only the member's own "Open my store" (?s=X&mode=sell) activates the
    // checkout intercept + QR share modal. A plain ?s=X link (what buyers
    // receive) just gets attribution written to the cart, then stays out of
    // the way so the buyer can check out normally.
    if (isSellMode) {
      interceptCheckout();
      blockCardNavigation();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
